import { HttpErrorResponse } from '@angular/common/http';

/**
 * RFC 9457 ProblemDetails as returned by the Vigilant API on failures, including any
 * extension members (e.g. `additionalProp1`). Surfaced verbatim so permission/validation
 * failures are legible during development.
 */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

const STANDARD_KEYS = new Set(['type', 'title', 'status', 'detail', 'instance']);

/** Coerce an unknown error (typically an HttpErrorResponse) into a ProblemDetails shape. */
export function asProblemDetails(err: unknown): ProblemDetails | null {
  if (err instanceof HttpErrorResponse) {
    const body = err.error;
    if (body && typeof body === 'object') return body as ProblemDetails;
    return {
      status: err.status || undefined,
      title: err.statusText || undefined,
      detail: typeof body === 'string' && body ? body : undefined,
    };
  }
  if (err && typeof err === 'object') return err as ProblemDetails;
  return null;
}

/**
 * Render a ProblemDetails as a human-readable multi-line message: a `title (status)` header,
 * the `detail`, then any non-standard extension members. Falls back to `fallback` when the
 * error carries nothing useful.
 */
export function formatProblemDetails(err: unknown, fallback = 'Request failed'): string {
  const pd = asProblemDetails(err);
  if (!pd) return fallback;

  const lines: string[] = [];
  const head = pd.title || pd.detail || fallback;
  lines.push(pd.status ? `${head} (${pd.status})` : head);
  if (pd.detail && pd.detail !== pd.title) lines.push(pd.detail);

  for (const [key, value] of Object.entries(pd)) {
    if (STANDARD_KEYS.has(key)) continue;
    // Per-field validation errors (RFC 9457 / ASP.NET `errors`): { field: string[] }.
    if (key === 'errors' && value && typeof value === 'object') {
      for (const messages of Object.values(value as Record<string, unknown>)) {
        for (const msg of Array.isArray(messages) ? messages : [messages]) {
          if (msg) lines.push(String(msg));
        }
      }
      continue;
    }
    if (value == null || value === '' || typeof value === 'object') continue;
    lines.push(`${key}: ${value}`);
  }
  return lines.join('\n');
}
